import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl} from "@angular/forms";
import {UserRole, UserWithStringRole} from "../../_types/user";
import {AuthService} from "../../_services/auth.service";
import {debounceTime, filter, of, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {SnackbarService} from "../../_services/snackbar.service";
import {AdminService} from "../../_services/admin.service";

@Component({
    selector: 'app-user-search',
    standalone: false,
    templateUrl: './user-search.component.html',
    styleUrl: './user-search.component.scss',
})
export class UserSearchComponent {
    @Input() title: string = 'Select User';
    @Input() placeholder: string = 'Select User';
    @Input() disabled: boolean = false;
    @Input() value: UserWithStringRole | undefined;
    @Input() excludeAdmins: boolean = false;
    @Input() filter: Record<string, any> | undefined = undefined;
    @Output() userSelect: EventEmitter<UserWithStringRole | undefined> = new EventEmitter();

    isRaa: boolean = false;
    userSearchControl: FormControl = new FormControl();
    loadingUsers: boolean = false;
    filteredUsers: UserWithStringRole[] = [];
    roles: UserRole[] = [];

    constructor(private authService: AuthService,
                private snackbarService: SnackbarService,
                private adminService: AdminService) {

        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
        if (this.excludeAdmins) {
            this.adminService.getUserRoles().subscribe({
                next: r => {
                    this.roles = r;
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        }
        this.userSearchControl.valueChanges.pipe(
            debounceTime(300),
            filter((value): value is string => typeof value === 'string' && value.length > 2),
            tap(() => this.loadingUsers = true),
            switchMap(value => {
                let filters: any = {
                    order_by: 'full_name',
                    direction: 'asc',
                    deactivated: false
                };

                if (this.excludeAdmins) {
                    const adminRole = this.roles.find((x: UserRole) => x.name === 'Admin');
                    if (adminRole) {
                        filters['excluded_roles'] = adminRole.id;
                    }
                }


                filters = Object.assign(filters, this.filter);

                return this.adminService.getAllUsers(0, 100000, value, filters).pipe(
                    catchError(e => {
                        this.snackbarService.openSnackBar(e, 'error');
                        return of({results: [] as UserWithStringRole[]});
                    })
                );
            })
        ).subscribe(r => {
            this.loadingUsers = false;
            this.filteredUsers = Array.from(
                new Map(r.results.map(user => [user.id, user])).values()
            );
        });

    }

    ngOnChanges(): void {
        if (this.value) {
            this.userSearchControl.setValue(this.value, {emitEvent: false});
        } else {
            this.userSearchControl.setValue(null, {emitEvent: false});
        }

        if (this.disabled) {
            this.userSearchControl.disable({emitEvent: false});
        } else {
            this.userSearchControl.enable({emitEvent: false});
        }
    }

    selectUserById(userId: number): void {
        const user = this.filteredUsers.find(x => x.id === userId);

        if (!user) {
            return;
        }

        this.userSearchControl.setValue(user, {emitEvent: false});
        this.filteredUsers = [];
        this.userSelect.emit(user);
    }

    displayFn = (value: UserWithStringRole | number | null): string => {
        if (!value) {
            return '';
        }

        if (typeof value === 'number') {
            const user = this.filteredUsers.find(x => x.id === value);
            return user
                ? this.authService.getDisplayName(user, !this.isRaa ? 'username' : undefined)
                : '';
        }

        return this.authService.getDisplayName(value, !this.isRaa ? 'username' : undefined);
    };

    nameDisplay(user: UserWithStringRole): string {
        return this.authService.getDisplayName(user, this.isRaa ? 'reference_number' : undefined)
    }
}
